import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { withTimeout } from '../utils/Timeout';

const API = process.env.REACT_APP_API_BASE_URL;

// Reviews for a product page. Public, no auth.
export const fetchProductReviews = createAsyncThunk(
  'reviews/fetchProductReviews',
  async ({ productId, offset = 0, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await withTimeout(
        axios.get(`${API}rest/public/reviews/product/${productId}`, {
          params: { offset, limit },
        }),
        10000
      );
      return { ...response.data, append: offset > 0 };
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Resolves an emailed review-invite token into the items that buyer can still rate.
export const fetchReviewInvite = createAsyncThunk(
  'reviews/fetchReviewInvite',
  async (token, { rejectWithValue }) => {
    try {
      const response = await withTimeout(
        axios.get(`${API}rest/public/reviews/invite/${token}`),
        10000
      );
      return response.data;
    } catch (error) {
      const data = error.response?.data;
      return rejectWithValue({
        reason: data?.reason || 'UNKNOWN',
        message: data?.message || 'We could not open this review link.',
      });
    }
  }
);

export const submitReviews = createAsyncThunk(
  'reviews/submitReviews',
  async ({ token, submissions }, { rejectWithValue }) => {
    try {
      const response = await withTimeout(
        axios.post(`${API}rest/public/reviews/invite/${token}`, submissions),
        15000
      );
      return response.data;
    } catch (error) {
      const data = error.response?.data;
      return rejectWithValue({
        reason: data?.reason || 'UNKNOWN',
        message: data?.message || 'We could not save your review. Please try again.',
      });
    }
  }
);

const initialState = {
  // product page
  byProduct: {
    reviews: [],
    averageRating: 0,
    reviewCount: 0,
    distribution: {},
    hasMore: false,
    productId: null,
  },
  loading: false,
  error: null,

  // invite page
  invite: null,
  inviteLoading: false,
  inviteError: null,
  submitting: false,
  submitError: null,
  submitted: false,
};

const reviewSlice = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    clearProductReviews: (state) => {
      state.byProduct = initialState.byProduct;
      state.error = null;
    },
    clearInviteState: (state) => {
      state.invite = null;
      state.inviteError = null;
      state.submitError = null;
      state.submitted = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProductReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductReviews.fulfilled, (state, action) => {
        const { reviews, append, ...summary } = action.payload;
        state.byProduct = {
          ...summary,
          reviews: append ? [...state.byProduct.reviews, ...reviews] : reviews,
        };
        state.loading = false;
      })
      .addCase(fetchProductReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      .addCase(fetchReviewInvite.pending, (state) => {
        state.inviteLoading = true;
        state.inviteError = null;
      })
      .addCase(fetchReviewInvite.fulfilled, (state, action) => {
        state.invite = action.payload;
        state.inviteLoading = false;
      })
      .addCase(fetchReviewInvite.rejected, (state, action) => {
        state.inviteLoading = false;
        state.inviteError = action.payload;
      })

      .addCase(submitReviews.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
      })
      .addCase(submitReviews.fulfilled, (state) => {
        state.submitting = false;
        state.submitted = true;
      })
      .addCase(submitReviews.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload;
      });
  },
});

export const { clearProductReviews, clearInviteState } = reviewSlice.actions;
export default reviewSlice.reducer;
